import React, {useEffect, useState} from "react";
import Card from "components/Cards/Card";
import reviewSummaryIcon from "assets/Icon_Tab_08.png";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import ToggleButton from "components/ui/ToggleButton";
import AutoSizer from "react-virtualized-auto-sizer";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import trendScoresIcon from "../../../assets/Icon_Tab_09.png";
import {Dropdown} from "../../ui/Dropdown";
import LoadingIndicator from "components/ui/LoadingIndicator";
import PortalTooltip from "../PortalTooltip";

type SummaryOfReviewsProps = {
  href: string | null;
  data: ChartData;
  defaultChartType: string;
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number
  isLoading?: boolean; // Add loading prop
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
  sources?: string[];
};

const chartColours = [
  "#307bb9",
  "#30b862",
  "#a440a4",
  "#d5a438",
  "#bb4040",
  "#008272",
  "#4068b4",
  "#7ebce6",
  "#8fba34",
  "#f64250",
  "#fbbf22",
  "#94adbf",
];

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

const Tick: React.FC<any> = ({
  payload: { value },
  verticalAnchor,
  visibleTicksCount,
  ...rest
}) => (
  <Text
    {...rest}
    verticalAnchor={verticalAnchor}
    visibleTicksCount={visibleTicksCount}
    width={200}
  >
    {value}
  </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

function SummaryOfRevs(props: SummaryOfReviewsProps): JSX.Element {
  const { href, data, defaultChartType, customName, setImages, idx, isLoading } = props;

  const chartData = data;
  const [internalLoading, setInternalLoading] = useState(true);

  const chartTypeId = `summaryOfReviews_chartType_${customName || "default"}`;
  const togglesId = `summaryOfReviews_toggles_${customName || "default"}`;

  const [summaryOfReviewsChartType, setSummaryOfReviewsChartType] = useState(
    () => getChartSelection(chartTypeId) || defaultChartType || "Area"
  );

  const [summaryOfReviewsToggle, setSummaryOfReviewsToggle] = useState<
    Record<string, boolean>
  >({});

  const handleSetSummaryOfReviewsToggle = (t: any) => {
    setSummaryOfReviewsToggle((summaryOfReviewsToggle) => {
      const toggles = { ...summaryOfReviewsToggle };
      toggles[t] = !toggles[t];
      setChartSelection(togglesId, toggles);
      return toggles;
    });
  };

  const handleSetSummaryOfReviewsChartType = (value: string) => {
    setSummaryOfReviewsChartType(value);
    setChartSelection(chartTypeId, value);
  };

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);
  const showLoading = isLoading || internalLoading;


  useEffect(() => {
    //let counter = 1;
    const sources: Record<string, boolean> = {};
    if (data && data.sources) {
      for (const source of data.sources) {
        //sources[source] = counter === 1;
        sources[source] = true;
        //counter++;
      }
    }
    const storedToggles = getChartSelection(togglesId);
    if (storedToggles && typeof storedToggles === "object") {
      const merged: Record<string, boolean> = {};
      for (const key of Object.keys(sources)) {
        merged[key] = storedToggles.hasOwnProperty(key) ? storedToggles[key] : sources[key];
      }
      setSummaryOfReviewsToggle(merged);
      setChartSelection(togglesId, merged);
    } else {
      setSummaryOfReviewsToggle(sources);
      setChartSelection(togglesId, sources);
    }
  }, [data]);

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const storedChartType = getChartSelection(chartTypeId);
      if (storedChartType) setSummaryOfReviewsChartType(storedChartType);
      const storedToggles = getChartSelection(togglesId);
      if (storedToggles) setSummaryOfReviewsToggle(storedToggles);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartTypeId, togglesId]);

  const uuid = uuidv4();

  const [getPng, { ref }] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        // Verify that png is not undefined
        // console.log("runchart");
        if (png && setImages && idx) {
          // console.log("savechart");
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      }
      catch(error) {
        // console.log(error)
      }
    }, 1000)
  }

  /*useEffect(() => {
    if (!savedPng) {
      saveChart();
    }
  }, [savedPng, data])*/
  if (showLoading) {
    return (
        <Card
            href={href}
            icon={reviewSummaryIcon}
            title={customName ? customName : "SUMMARY OF REVIEWS"}
            chartOptions={
              <React.Fragment>
                <button
                    className="Option"
                    onClick={() => handleSetSummaryOfReviewsChartType("Area")}
                >
                  Area
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetSummaryOfReviewsChartType("Bar")}
                >
                  Bar
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetSummaryOfReviewsChartType("Line")}
                >
                  Line
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetSummaryOfReviewsChartType("Pie")}
                >
                  Pie
                </button>
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div className="ToggleFlex">
              <div className="BarToggle">
                {summaryOfReviewsToggle &&
                    Object.entries(summaryOfReviewsToggle).map(
                        ([key, value], index: number) => {
                          return (
                              <ToggleButton
                                  key={index}
                                  active={value}
                                  toggle={() => handleSetSummaryOfReviewsToggle(key)}
                              >
                                <div
                                    className="ToggleDot"
                                    style={{
                                      backgroundColor: chartColours[index],
                                    }}
                                ></div>
                                <div className="ToggleLabel">{key}</div>
                              </ToggleButton>
                          );
                        }
                    )}
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator/>
            </div>
          </div>
        </Card>
    );
  }

  return (
      <Card
          href={href}
          icon={reviewSummaryIcon}
          title={customName ? customName : "SUMMARY OF REVIEWS"}
          chartOptions={
            <React.Fragment>
              <button
                  className="Option"
                  onClick={() => handleSetSummaryOfReviewsChartType("Area")}
              >
                Area
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetSummaryOfReviewsChartType("Bar")}
              >
                Bar
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetSummaryOfReviewsChartType("Line")}
              >
                Line
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetSummaryOfReviewsChartType("Pie")}
              >
                Pie
              </button>
            </React.Fragment>
          }
    >
      <div className="CardBody">
        <div className="ToggleFlex">
          <div className="BarToggle">
            {summaryOfReviewsToggle &&
              Object.entries(summaryOfReviewsToggle).map(
                ([key, value], index: number) => {
                  return (
                    <ToggleButton
                      key={index}
                      active={value}
                      toggle={() => handleSetSummaryOfReviewsToggle(key)}
                    >
                      <div
                        className="ToggleDot"
                        style={{
                          backgroundColor: chartColours[index],
                        }}
                      ></div>
                      <div className="ToggleLabel">{key}</div>
                    </ToggleButton>
                  );
                }
              )}
          </div>
        </div>
        <div className="Chart"  style={{height: "380px"}}>
          <div className="ChartWrapper">
            {data && checkNullChartData(data.pieChartData.data) ? (
              <div
                className={(() => {
                  const mw = ((data.sources &&
                      summaryOfReviewsChartType === "Area") ||
                    summaryOfReviewsChartType === "Line"
                      ? 100
                      : data.sources && data.sources.length > 1
                      ? Object.values(summaryOfReviewsToggle).filter((v: any) => v)
                          .length > 1
                        ? data.sources.length * 50
                        : 100
                      : 100) * data.pieChartData.questions.length;
                  return mw > 400 ? "scrollable-chart" : undefined;
                })()}
                style={{
                  minWidth:
                    `${((data.sources &&
                      summaryOfReviewsChartType === "Area") ||
                    summaryOfReviewsChartType === "Line"
                      ? 100
                      : data.sources && data.sources.length > 1
                      ? Object.values(summaryOfReviewsToggle).filter((v: any) => v)
                          .length > 1
                        ? data.sources.length * 50
                        : 100
                      : 100) * data.pieChartData.questions.length}px`
              }}
              >
                {summaryOfReviewsChartType === "Area" ?
                    <div style={{height: "360px"}}>
                    <AutoSizer>
                      {({width,height}) =>
                          <AreaChart
                          data={data.otherChartData}
                          margin={{ top: 20, bottom: 20, right: 20 }}
                          ref={ref}
                          width={width}
                          height={height}
                        >
                        <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 150, right: 150 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        }
                        domain={
                          data.options.yAxisType === "Percentage"
                            ? [0, 100]
                            : undefined
                        }
                        ticks={
                          data.options.yAxisType === "Percentage"
                            ? ticks20
                            : undefined
                        }
                      />
                      {summaryOfReviewsToggle &&
                        Object.entries(summaryOfReviewsToggle).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Area
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={chartColours[index]}
                                  dot={true}
                                  width={600}
                                  height={600}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={
                                      data.options.yAxisType === "Percentage"
                                        ? percentageFormatter
                                        : undefined
                                    }
                                  />
                                </Area>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                    </AreaChart>}
                    </AutoSizer></div>
                   : summaryOfReviewsChartType === "Bar" ? (
                       <div style={{height: "360px"}}>
                    <AutoSizer>
                      {({width,height}) =>
                          <BarChart
                      data={chartData.otherChartData}
                      margin={{ top: 20, bottom: 20, right: 20 }}
                      maxBarSize={65}
                      barGap={15}
                      ref={ref}
                      width={(width ? width : 100) - 1}
                      height={height || 300}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={
                          chartData.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        }
                        domain={
                          chartData.options.yAxisType === "Percentage"
                            ? [0, 100]
                            : undefined
                        }
                        ticks={
                          chartData.options.yAxisType === "Percentage"
                            ? ticks20
                            : undefined
                        }
                      />
                      {summaryOfReviewsToggle &&
                        Object.entries(summaryOfReviewsToggle).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Bar
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  radius={[3, 3, 0, 0]}
                                  isAnimationActive={false}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={
                                      chartData.options.yAxisType ===
                                      "Percentage"
                                        ? percentageFormatter
                                        : undefined
                                    }
                                  />
                                </Bar>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                        </BarChart>}
                      </AutoSizer>

                       </div>) : summaryOfReviewsChartType === "Line" ? (
                    <AutoSizer>
                      {({width,height}) =>

                          <LineChart
                      data={data.otherChartData}
                      margin={{ top: 20, bottom: 20, right: 20 }}
                      ref={ref}
                      width={width}
                      height={height}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 150, right: 150 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        }
                        domain={
                          data.options.yAxisType === "Percentage"
                            ? [0, 100]
                            : undefined
                        }
                        ticks={
                          data.options.yAxisType === "Percentage"
                            ? ticks20
                            : undefined
                        }
                      />
                      {summaryOfReviewsToggle &&
                        Object.entries(summaryOfReviewsToggle).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Line
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={chartColours[index]}
                                  dot={{
                                    stroke: chartColours[index],
                                    strokeWidth: 2,
                                  }}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={
                                      data.options.yAxisType === "Percentage"
                                        ? percentageFormatter
                                        : undefined
                                    }
                                  />
                                </Line>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                    </LineChart>
                      }
                    </AutoSizer>
                  ) : summaryOfReviewsChartType === "Pie" ? (
                    <div className="PieFlex">
                      {data.pieChartData.data.map((p: any, i: number) => {
                        const noData = "No data available";
                        let newP: any = [];
                        if (p) {
                          for (const dataPoint of p) {
                            const newPoint = { ...dataPoint };
                            if (!summaryOfReviewsToggle[newPoint.label]) {
                              newPoint.count = 0;
                            }
                            newP.push(newPoint);
                          }
                        } else newP = null;
                        return (
                          <div key={i}>
                            {newP ? (
                              <PieChart width={270} height={220} ref={ref}>
                                <Pie
                                  data={newP}
                                  dataKey="count"
                                  nameKey="label"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={80}
                                  legendType="circle"
                                  stroke="none"
                                  label={({ value }) => {
                                    if (!isNaN(value) && value !== 0) {
                                      if (
                                        data?.options.yAxisType === "Percentage"
                                      ) {
                                        return `${value}%`;
                                      } else {
                                        return value;
                                      }
                                    } else {
                                      return "";
                                    }
                                  }}
                                  labelLine={false}
                                  isAnimationActive={false}
                                >
                                  {chartColours.map(
                                    (colour: string, index: number) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={colour}
                                      />
                                    )
                                  )}
                                </Pie>
                                <Tooltip content={<PortalTooltip formatter={
                                    data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : undefined
                                  } />} />
                              </PieChart>
                            ) : (
                              <PieChart width={250} height={220} ref={ref}>
                                <text
                                  x="50%"
                                  y="50%"
                                  textAnchor="middle"
                                  fontSize={14}
                                  fontFamily="Arial"
                                  fontWeight={400}
                                >
                                  {noData}
                                </text>
                              </PieChart>
                            )}
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  margin: "10px auto",
                                  display: "block",
                                  width: "200px",
                                }}
                              >
                                {data ? data.pieChartData.questions[i] : ""}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <React.Fragment />
                  )}
              </div>
            ) : (
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

//export default SummaryOfReviews;
export const SummaryOfReviews = React.memo(SummaryOfRevs);
